// Core
import React, { useMemo } from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Types
import { EquipmentUnitDataConeCrusher } from '../../../../../../types/crusher';
import { MeasurementType } from '../../../../../../types/global';

// Plugins
import { isDakotaModel, lastOperationalUnitDataCone } from '../../../../../../plugins/crusher';

// Components
import {
  LineChart,
  LineChartType,
  Series,
} from '../../../../../../layout/elements/LineChart';

// View
import { LubeUnitProps } from './LubeUnit.types';
import { ChartMode } from '../Trendings.types';

function LubeUnit({
  className,
  chartMode,
  operationalData,
  testId,
  trendingLubeUnitData,
}: LubeUnitProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  // Check if the model is Dakota
  const lastUnitData: EquipmentUnitDataConeCrusher | null = lastOperationalUnitDataCone(
    operationalData,
  );
  const isDakota: boolean = isDakotaModel(lastUnitData?.crusherModelPLC);

  // Generate dynamic ID based on enabled sensors and chart mode to force chart remount
  const chartId = useMemo((): string => {
    const sensorsState: string = [
      lastUnitData?.lubeSupplyTempEnable || false,
      isDakota,
    ].map(Boolean).join('-');

    const baseId: string = `lube-unit-${chartMode}`;
    return `${baseId}-${sensorsState}`;
  }, [
    chartMode,
    lastUnitData?.lubeSupplyTempEnable,
    isDakota,
  ]);

  const series: Series[] = useMemo(() => [
    {
      name: t('common.cloudConnection'),
      field: 'edgeStatus',
      type: LineChartType.fill,
      date: 'timestamp',
      opposite: true,
      measurementType: MeasurementType.temperature,
    },
    ...(lastUnitData?.lubeSupplyTempEnable ? [{
      name: t('view.crusher.trendings.lubeUnit.legend.supplyTemp'),
      field: 'lubricationSupplyTemperature',
      type: LineChartType.line,
      date: 'timestamp',
      unit: '°C',
      decimals: 2,
      measurementType: MeasurementType.temperature,
      bullet: true,
      minScale: 0,
      maxScale: 60,
    }] : []),
    {
      name: t('view.crusher.trendings.lubeUnit.legend.returnTemp'),
      field: 'lubricationReturnTemperature',
      type: LineChartType.line,
      date: 'timestamp',
      unit: '°C',
      decimals: 2,
      bullet: true,
      measurementType: MeasurementType.temperature,
      minScale: 0,
      maxScale: 60,
    },
    ...(!isDakota ? [{
      name: t('view.crusher.trendings.lubeUnit.legend.lubePressure'),
      field: 'lubricationOilPressure',
      type: LineChartType.line,
      date: 'timestamp',
      unit: 'PSI',
      opposite: true,
      decimals: 2,
      bullet: true,
      measurementType: MeasurementType.pressure,
      minScale: 0,
      maxScale: 0.27579,
    }] : []),
    {
      name: t('view.crusher.trendings.lubeUnit.legend.tankTemp'),
      field: 'lubricationTankTemperature',
      type: LineChartType.line,
      date: 'timestamp',
      unit: '°C',
      decimals: 2,
      bullet: true,
      measurementType: MeasurementType.temperature,
      minScale: 0,
      maxScale: 60,
    },
  ], [
    t,
    lastUnitData?.lubeSupplyTempEnable,
    isDakota,
  ]);

  return (
    <LineChart
      activeBullet={chartMode === ChartMode.live || chartMode === ChartMode.liveAggregate}
      className={className}
      data={trendingLubeUnitData}
      id={chartId}
      series={series}
      testId={testId}
    />
  );
}

export { LubeUnit };
